import React, { useState, useEffect } from 'react';
import './SelectPremium.css';

export default function SelectPremium({ premiums, setSelectedPremium }) {
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedCoverage, setSelectedCoverage] = useState('CHP'); // Default to CHP
  const [familyMembers, setFamilyMembers] = useState({
    includeSpouse: false,
    childrenCount: 0
  });

  const handleSelect = (frequency) => {
    setSelectedFrequency(frequency);
    setSelectedCoverage('CHP'); // Reset to CHP on new selection
    // Send the selected plan and family composition to parent
    setSelectedPremium({
      ...frequency, 
      selectedCoverage,
      familyMembers,
      totalPremium: calculateTotalPremium(frequency)
    });
  };

  const handleFamilyChange = (type, value) => {
    const newFamilyMembers = {...familyMembers};
    
    if (type === 'spouse') {
      newFamilyMembers.includeSpouse = value;
    } else if (type === 'children') {
      newFamilyMembers.childrenCount = value;
    }
    
    setFamilyMembers(newFamilyMembers);
    
    // Update parent component if a frequency is already selected
    if (selectedFrequency) {
      setSelectedPremium({
        ...selectedFrequency,
        selectedCoverage,
        familyMembers: newFamilyMembers,
        totalPremium: calculateTotalPremium(selectedFrequency, newFamilyMembers)
      });
    }
  };

  const calculateTotalPremium = (frequency, members = familyMembers) => {
    let total = frequency.CHP; // Main member (CHP) is always included
    
    if (members.includeSpouse) {
      total += frequency.SPOUSE;
    }
    
    if (members.childrenCount > 0) {
      total += frequency.CHILD * members.childrenCount;
    }
    
    return total;
  };

  const calculateSavings = (frequency, familyComp = familyMembers) => {
    // Calculate monthly equivalent for current frequency
    const divider = frequency.frequency === 'Annual' ? 12 : 
                   frequency.frequency === 'Semi-Annual' ? 6 : 
                   frequency.frequency === 'Quarterly' ? 3 : 1;
    
    // Get monthly plan
    const monthlyPlan = premiums.find(p => p.frequency === 'Monthly');
    
    if (!monthlyPlan || frequency.frequency === 'Monthly') {
      return { monthlyEquivalent: calculateTotalPremium(frequency, familyComp) / divider, savings: 0 };
    }
    
    // Calculate what the same family composition would cost monthly
    const monthlyTotal = calculateTotalPremium(monthlyPlan, familyComp);
    
    // Calculate what the same family composition costs with this frequency, converted to monthly
    const thisFrequencyMonthly = calculateTotalPremium(frequency, familyComp) / divider;
    
    // Calculate savings
    const monthlySavings = monthlyTotal - thisFrequencyMonthly;
    const totalSavings = monthlySavings * divider;
    
    return { 
      monthlyEquivalent: thisFrequencyMonthly, 
      savings: totalSavings,
      monthlySavings: monthlySavings
    };
  };

  // Calculate the best value plan based on current family composition
  const bestValuePlan = premiums.reduce((best, current) => {
    if (current.frequency === 'Monthly') return best;
    
    const { monthlyEquivalent: bestEquiv } = calculateSavings(best);
    const { monthlyEquivalent: currentEquiv } = calculateSavings(current);
    return currentEquiv < bestEquiv ? current : best;
  }, premiums.find(p => p.frequency !== 'Monthly') || premiums[0]);

  // Pass initial values to parent component on first render
  useEffect(() => {
    if (premiums && premiums.length > 0) {
      setSelectedPremium({
        ...premiums[0],
        selectedCoverage,
        familyMembers,
        totalPremium: calculateTotalPremium(premiums[0])
      });
    }
  }, []);

  return (
    <div className="select-premium-container">
      <h2 className="premium-header">Select Your Payment Frequency</h2>
      
      <div className="family-composition">
        <h3 className="family-title">Family Composition</h3>
        
        <div className="family-options">
          <div className="family-option compact">
            <span className="family-label">CHP</span>
            <span className="family-included">Always included</span>
          </div>
          
          <div className="family-option compact">
            <span className="family-label">Include Spouse</span>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={familyMembers.includeSpouse}
                onChange={(e) => handleFamilyChange('spouse', e.target.checked)}
              />
              <span className="slider round"></span>
            </label>
          </div>
          
          <div className="family-option compact">
            <span className="family-label">Children</span>
            <div className="children-counter">
              <button
                type="button"
                className="counter-btn"
                disabled={familyMembers.childrenCount <= 0}
                onClick={() => handleFamilyChange('children', Math.max(0, familyMembers.childrenCount - 1))}
              >-</button>
              <span className="counter-value">{familyMembers.childrenCount}</span>
              <button
                type="button"
                className="counter-btn"
                onClick={() => handleFamilyChange('children', familyMembers.childrenCount + 1)}
              >+</button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="premium-options-wrapper">
        {premiums.map((premium, index) => {
          const { monthlyEquivalent, savings, monthlySavings } = calculateSavings(premium);
          const isBestValue = premium === bestValuePlan && premium.frequency !== 'Monthly';
          const totalPremium = calculateTotalPremium(premium);
          
          return (
            <div
              key={index}
              className={`premium-option ${selectedFrequency === premium ? 'selected' : ''} ${isBestValue ? 'best-value' : ''}`}
              onClick={() => handleSelect(premium)}
            >
              {isBestValue && <div className="best-value-badge">Best Value</div>}
              <h3 className="frequency-title">{premium.frequency}</h3>
              
              <div className="premium-breakdown">
                <div className="breakdown-item">
                  <span className="breakdown-name">CHP:</span>
                  <span className="breakdown-price">Ksh {premium.CHP}</span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-name">Spouse:</span>
                  <span className="breakdown-price">Ksh {premium.SPOUSE}</span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-name">Per Child:</span>
                  <span className="breakdown-price">Ksh {premium.CHILD}</span>
                </div>
              </div>
              
              <div className="premium-price">
                <span className="currency">Ksh</span>
                <span className="amount">{totalPremium}</span>
                <span className="frequency-label">{premium.frequency.toLowerCase()}</span>
              </div>
              
              <div className="premium-details">
                <div className="detail-item">
                  <span className="detail-label">Monthly Equivalent:</span>
                  <span className="detail-value">Ksh {monthlyEquivalent.toFixed(2)}</span>
                </div>
                
                {savings > 0 && (
                  <>
                    <div className="detail-item savings">
                      <span className="detail-label">Monthly Savings:</span>
                      <span className="detail-value">Ksh {monthlySavings.toFixed(2)}</span>
                    </div>
                    <div className="detail-item savings total-savings">
                      <span className="detail-label">Total Savings:</span>
                      <span className="detail-value">Ksh {savings.toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                <button type="button" className="select-btn">
                  {selectedFrequency === premium ? 'Selected' : 'Select Plan'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="premium-note">* Annual and semi-annual plans offer significant savings compared to monthly payments</p>
      <p className="premium-note">* Children coverage price is per child</p>
    </div>
  );
}